import {LeanDocument, Types} from 'mongoose';
import {API} from './api.class';
import {CreateWarehouseOrderRequestDto} from '@/classes/dto/warehouse-manager/request/CreateWarehouseOrder.request.dto';
import {WarehouseOrder} from '@/interfaces/warehouseOrder.interface';
import {
    GetArticlesWithPendingOrdersRequestDto,
} from '@/classes/dto/warehouse-manager/request/GetArticlesWithPendingOrders.request.dto';
import {GetOrderNumbersRequestDto} from '@/classes/dto/warehouse-manager/request/GetOrderNumbers.request.dto';
import {
    GetArticlesWithPendingOrdersResponseDto,
} from '@/classes/dto/warehouse-manager/response/GetArticlesWithPendingOrders.response.dto';
import {GetOrderNumbersResponseDto} from '@/classes/dto/warehouse-manager/response/GetOrderNumbers.response.dto';
import {GetStockRequestDto} from '@/classes/dto/warehouse-manager/request/GetStock.request.dto';
import {FindWarehouseOrderRequestDto} from '@/classes/dto/warehouse-manager/request/FindWarehouseOrder.request.dto';
import {FindByIdOptions} from '@/interfaces/findByIdOptions';
import {UpdateWarehouseOrderRequestDto} from '@/classes/dto/warehouse-manager/request/UpdateWarehouseOrder.request.dto';
import {
    MassFulfillWarehouseOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/MassFulfillWarehouseOrder.request.dto';
import {BulkUpdateResponseDto} from '@/classes/dto/_common/response/bulkUpdate.response.dto';
import {
    BulkUpdateWarehouseOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/BulkUpdateWarehouseOrder.request.dto';
import {FindByIdOptionsRequestDto} from '@/classes/dto/_common/request/FindByIdOptions.request.dto';
import {
    CreateProductOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-order/CreateProductOrder.request.dto';
import {
    FindProductOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-order/FindProductOrder.request.dto';
import {ProductOrderDocument} from '@/documentTypes/warehouse-manager/ProductOrder.document';
import {
    UpdateProductOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-order/UpdateProductOrder.request.dto';
import {ProductShipmentDocument} from '@/documentTypes/warehouse-manager/ProductShipment.document';
import {
    CreateProductShipmentsFromOrderRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment/CreateProductShipmentsFromOrder.request.dto';
import {
    FindProductShipmentRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment/FindProductShipment.request.dto';
import {
    BulkCreateProductShipmentRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment/BulkCreateProductShipment.request.dto';
import {
    UpdateProductShipmentRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment/UpdateProductShipment.request.dto';
import {
    GetProductOrderInventoryResponseDto,
} from '@/classes/dto/warehouse-manager/response/product-order/GetProductOrderInventory.response.dto';
import {
    GetProductOrderInventoryRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-order/GetProductOrderInventory.request.dto';
import {
    CreateProductShipmentBundleRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment-bundle/CreateProductShipmentBundle.request.dto';
import {ProductShipmentBundleDocument} from '@/documentTypes/warehouse-manager/ProductShipmentBundle.document';
import {PaginationResponseType} from '@/types/common/paginationResponseType';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {
    FindProductShipmentBundleRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-shipment-bundle/FindProductShipmentBundle.request.dto';
import {
    GetProductOrderRequiredStockRequestDto,
} from '@/classes/dto/warehouse-manager/request/product-order/GetProductOrderRequiredStock.request.dto';
import {
    GetProductOrderRequiredStockResponseDto,
} from '@/classes/dto/warehouse-manager/response/product-order/GetProductOrderRequiredStock.response.dto';

/**
 * This class is used for communicating with the warehouse manager microservice.
 * @author Joshua Seipel
 */
export class WarehouseManagerAPI extends API {
    static service = 'warehouse-manager';

    /**
     * @deprecated
     */
    static async createOrder(order: CreateWarehouseOrderRequestDto): Promise<WarehouseOrder> {
        return this.post(`warehouse-order/`, order);
    }

    /**
     * @deprecated
     */
    static async getStock(articleId: Types.ObjectId, data: GetStockRequestDto): Promise<number> {
        return this.get(`warehouse-order/stock/${articleId}`, data);
    }

    /**
     * @deprecated
     */
    static async findById(id: Types.ObjectId, options?: FindByIdOptions): Promise<WarehouseOrder> {
        return this.get(`warehouse-order/${id}`, options);
    }

    /**
     * @deprecated
     */
    static async find(data: FindWarehouseOrderRequestDto): Promise<{
        warehouseOrders: WarehouseOrder[];
        maxPage: number
    }> {
        return this.get(`warehouse-order`, data);
    }

    /**
     * @deprecated
     */
    static async massFulfill(update: MassFulfillWarehouseOrderRequestDto): Promise<void> {
        return this.put('warehouse-order/mass-fulfill', update);
    }

    /**
     * @deprecated
     */
    static async update(id: Types.ObjectId, update: UpdateWarehouseOrderRequestDto): Promise<void> {
        return this.put(`warehouse-order/${id}`, update);
    }

    /**
     * @deprecated
     */
    static async bulkUpdate(bulkUpdateData: BulkUpdateWarehouseOrderRequestDto): Promise<BulkUpdateResponseDto> {
        return this.put(`warehouse-order/bulk`, bulkUpdateData);
    }

    /**
     * @deprecated
     */
    static async getArticlesWithPendingOrders(data: GetArticlesWithPendingOrdersRequestDto): Promise<GetArticlesWithPendingOrdersResponseDto> {
        return this.get(`warehouse-order/aggregation/articles-with-pending-orders`, data);
    }

    /**
     * @deprecated
     */
    static async getOrderNumbers(data: GetOrderNumbersRequestDto): Promise<GetOrderNumbersResponseDto> {
        return this.get('warehouse-order/aggregation/order-numbers', data);
    }

    /**
     * @deprecated
     */
    static async remove(id: Types.ObjectId): Promise<void> {
        return this.delete(`warehouse-order/${id}`);
    }

    static productOrder = {
        async create(data: CreateProductOrderRequestDto): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.post('product-order', data);
        },
        async getRequiredStock(data: GetProductOrderRequiredStockRequestDto): Promise<GetProductOrderRequiredStockResponseDto> {
            return WarehouseManagerAPI.get('product-order/required-stock', data);
        },
        async getInventory(data: GetProductOrderInventoryRequestDto): Promise<GetProductOrderInventoryResponseDto> {
            return WarehouseManagerAPI.get('product-order/inventory', data);
        },
        async find(data: FindProductOrderRequestDto): Promise<{
            productOrders: LeanDocument<ProductOrderDocument>[];
            maxPage: number;
        }> {
            return WarehouseManagerAPI.get('product-order', data);
        },
        async findById(id: Types.ObjectId | string, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<ProductOrderDocument>> {
            return WarehouseManagerAPI.get(`product-order/${id}`, options);
        },
        async update(id: Types.ObjectId | string, data: UpdateProductOrderRequestDto): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.put(`product-order/${id}`, data);
        },
        async delete(id: Types.ObjectId | string): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.delete(`product-order/${id}`);
        },
    }

    static productShipment = {
        async create(data: CreateProductShipmentsFromOrderRequestDto): Promise<Types.ObjectId[]> {
            return WarehouseManagerAPI.post('product-shipment', data);
        },
        async bulkCreate(data: BulkCreateProductShipmentRequestDto): Promise<Types.ObjectId[]> {
            return WarehouseManagerAPI.post('product-shipment/bulk', data);
        },
        async find(data: FindProductShipmentRequestDto): Promise<{
            productShipments: LeanDocument<ProductShipmentDocument>[];
            maxPage: number;
        }> {
            return WarehouseManagerAPI.get('product-shipment', data);
        },
        async findById(id: Types.ObjectId | string, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<ProductShipmentDocument>> {
            return WarehouseManagerAPI.get(`product-shipment/${id}`, options);
        },
        async update(id: Types.ObjectId | string, data: UpdateProductShipmentRequestDto): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.put(`product-order/${id}`, data);
        },
        async delete(id: Types.ObjectId | string): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.delete(`product-shipment/${id}`);
        },
    }

    static productShipmentBundle = {
        async create(data: CreateProductShipmentBundleRequestDto): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.post('product-shipment-bundle', data);
        },
        async findById(id: Types.ObjectId | string, options?: FindByIdOptionsRequestDto): Promise<LeanDocument<ProductShipmentBundleDocument>> {
            return WarehouseManagerAPI.get(`product-shipment/${id}`, options);
        },
        async find(data: FindProductShipmentBundleRequestDto): Promise<PaginationResponseType<CrudEntityTypes.PRODUCT_SHIPMENT_BUNDLE, ProductShipmentBundleDocument>> {
            return WarehouseManagerAPI.get('product-shipment-bundle', data);
        },
        async delete(id: Types.ObjectId | string): Promise<Types.ObjectId> {
            return WarehouseManagerAPI.delete(`product-shipment-bundle/${id}`);
        },
    }
}
